// Auto-fits the current step and next-step preview text into the available
// area by shrinking font sizes. Runs after layout has settled (double rAF)
// so we measure the real height/width.
//
// Measurement uses explicit child heights — not line-clamp, which would
// under-report scrollHeight and leave clipped text in place.

import { els } from "./dom.js";

let currentStepFitRaf = null;

function parseGap(node) {
  if (!node) return 0;
  const style = getComputedStyle(node);
  return Number.parseFloat(style.rowGap || style.gap) || 0;
}

function fitTextPair({
  container,
  titleEl,
  bodyEl,
  extraHeight = 0,
  gapCount = 1,
  titleMax,
  titleMin,
  bodyMax,
  bodyMin,
  titleVar,
  bodyVar,
}) {
  if (!container || !titleEl || !bodyEl) return;
  if (!container.isConnected || container.clientHeight <= 0 || container.clientWidth <= 0) {
    return;
  }

  container.style.removeProperty(titleVar);
  container.style.removeProperty(bodyVar);
  container.dataset.textCompressed = "false";

  const gapValue = parseGap(container);
  const gapTotal = gapValue * gapCount;
  const maxHeight = container.clientHeight;

  function apply(titleSize, bodySize) {
    container.style.setProperty(titleVar, `${titleSize}px`);
    container.style.setProperty(bodyVar, `${bodySize}px`);
  }

  function neededHeight() {
    return extraHeight + gapTotal + titleEl.scrollHeight + bodyEl.scrollHeight;
  }

  function fits(titleSize, bodySize) {
    apply(titleSize, bodySize);
    return neededHeight() <= maxHeight + 1;
  }

  let bestTitle = titleMin;
  let bestBody = bodyMin;

  for (let bodySize = bodyMax; bodySize >= bodyMin; bodySize -= 1) {
    for (let titleSize = titleMax; titleSize >= titleMin; titleSize -= 1) {
      if (fits(titleSize, bodySize)) {
        apply(titleSize, bodySize);
        return;
      }
      bestTitle = titleSize;
      bestBody = bodySize;
    }
  }

  apply(bestTitle, bestBody);
  if (neededHeight() > maxHeight + 1) {
    container.dataset.textCompressed = "true";
  }
}

export function scheduleCurrentStepFit() {
  if (currentStepFitRaf) {
    cancelAnimationFrame(currentStepFitRaf);
  }
  currentStepFitRaf = requestAnimationFrame(() => {
    currentStepFitRaf = requestAnimationFrame(() => {
      currentStepFitRaf = null;
      fitCurrentStepText();
      fitNextStepText();
    });
  });
}

export function fitCurrentStepText() {
  const box = els.currentStepText;
  const title = els.currentStepTitle;
  const instruction = els.currentStepInstruction;
  const meta = box?.querySelector(".current-step-meta");

  if (!box || !title || !instruction) return;

  const collapsed = !!els.layoutRoot?.classList.contains("debug-collapsed");
  const boxHeight = box.clientHeight;
  const boxWidth = box.clientWidth;
  const metaHeight = meta?.offsetHeight || 0;
  const textBlockHeight = Math.max(0, boxHeight - metaHeight);

  fitTextPair({
    container: box,
    titleEl: title,
    bodyEl: instruction,
    extraHeight: metaHeight,
    gapCount: 2,
    titleMax: Math.round(
      Math.min(
        collapsed ? 86 : 72,
        Math.max(18, textBlockHeight * 0.46),
        Math.max(18, boxWidth * 0.09),
      ),
    ),
    titleMin: 16,
    bodyMax: Math.round(
      Math.min(
        collapsed ? 64 : 56,
        Math.max(14, textBlockHeight * 0.38),
        Math.max(14, boxWidth * 0.065),
      ),
    ),
    bodyMin: 12,
    titleVar: "--current-title-fit-size",
    bodyVar: "--current-instruction-fit-size",
  });
}

export function fitNextStepText() {
  const preview = els.nextStepPreview;
  const content = els.nextStepContent || preview?.querySelector(".next-step-content");
  const title = els.nextStepTitle;
  const instruction = els.nextStepInstruction;

  if (!content || !title || !instruction) return;

  const collapsed = !!els.layoutRoot?.classList.contains("debug-collapsed");
  const contentHeight = content.clientHeight;
  const contentWidth = content.clientWidth;

  fitTextPair({
    container: content,
    titleEl: title,
    bodyEl: instruction,
    titleMax: Math.round(
      Math.min(
        collapsed ? 30 : 18,
        Math.max(9, contentHeight * 0.38),
        Math.max(9, contentWidth * 0.05),
      ),
    ),
    titleMin: 9,
    bodyMax: Math.round(
      Math.min(
        collapsed ? 24 : 16,
        Math.max(9, contentHeight * 0.34),
        Math.max(9, contentWidth * 0.04),
      ),
    ),
    bodyMin: 9,
    titleVar: "--next-title-fit-size",
    bodyVar: "--next-instruction-fit-size",
  });
}
